import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Input } from '../../../elements/input';
import { FieldError } from '../../../elements/field-error/field-error';
import { Button } from '../../../elements/button';
import { useHttpClient } from '../../../shared/http-client';
import { TimePicker } from '../../../elements/date-time-picker/date-time-picker.element';

export const RoutesConfigForm = ({ item, isEdit }: { item?: any; isEdit: boolean }) => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const navigate = useNavigate();
    const { api, state } = useHttpClient();
    const [data, setData] = useState<any | null>(null);

    const validacion = Yup.object().shape({
        workday_start_time: Yup.string().required('obligatorio'),
        name: Yup.string().required('obligatorio'),
        workday_end_time: Yup.string().required('obligatorio'),
        break_start_time: Yup.string().required('obligatorio'),
        break_end_time: Yup.string().required('obligatorio'),
        break_duration_hours: Yup.number().typeError('obligatorio').required('obligatorio').min(0),
        estimated_visit_time_minutes: Yup.number().typeError('obligatorio').required('obligatorio').min(0),
        max_distance_per_day_km: Yup.number().typeError('obligatorio').required('obligatorio').min(0)
    });

    const initialValues: any = {
        name: '',
        workday_start_time: '00:00',
        workday_end_time: '00:00',
        break_start_time: '00:00',
        break_end_time: '00:00',
        break_duration_hours: 0,
        estimated_visit_time_minutes: 0,
        max_distance_per_day_km: 0
    };

    useEffect(() => {
        if (item) {
            setData(item);
        }
    }, [item]);

    const beforeSubmit = (values: any) => {
        const after = { ...values };
        after.break_duration_hours = +after.break_duration_hours;
        after.estimated_visit_time_minutes = +after.estimated_visit_time_minutes;
        after.max_distance_per_day_km = +after.max_distance_per_day_km;
        return after;
    };

    const onSubmit = async (values: any) => {
        const enviaremos = beforeSubmit(values);

        if (enviaremos.id) {
            api(`/fleet/route-configs/${enviaremos.id}/`, 'PATCH', enviaremos);
        } else {
            api('/fleet/route-configs/', 'POST', enviaremos);
        }
    };

    useEffect(() => {
        if (state.data && state.path?.includes('route-configs')) {
            if (state.method === 'POST' || state.method === 'PATCH') {
                toast.success(`${t('common.label.success-saved')}`);
                navigate(-1);
            }
        }
    }, [state]);

    return (
        <Formik initialValues={data || initialValues} onSubmit={onSubmit} validationSchema={validacion} enableReinitialize>
            {({ values, errors, touched, setFieldValue, handleSubmit, submitForm }) => (
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="w-1/3 pt-[5px]">
                        <Input
                            label={t('common.label.name')}
                            type="text"
                            placeholder="name"
                            name="name"
                            value={values.name}
                            onChange={(value) => setFieldValue('name', String(value.name ?? ''))}
                        />
                        <FieldError touched={touched} errors={errors} field={'name'} />
                    </div>
                    <div className="flex w-full justify-between">
                        <div className="w-full pt-[5px]">
                            <TimePicker
                                label={t('workday_start_time')}
                                name="workday_start_time"
                                value={values.workday_start_time}
                                onChange={(value) => setFieldValue('workday_start_time', value.workday_start_time)}
                            />
                            <FieldError touched={touched} errors={errors} field={'workday_start_time'} />
                        </div>

                        <div className="pl-8 w-full pt-[5px]">
                            <TimePicker
                                label={t('workday_end_time')}
                                name="workday_end_time"
                                value={values.workday_end_time}
                                onChange={(value) => setFieldValue('workday_end_time', value.workday_end_time)}
                            />
                            <FieldError touched={touched} errors={errors} field={'workday_end_time'} />
                        </div>
                    </div>

                    <div className="flex w-full justify-between">
                        <div className="w-full pt-[5px]">
                            <TimePicker
                                label={t('break_start_time')}
                                name="break_start_time"
                                value={values.break_start_time}
                                onChange={(value) => setFieldValue('break_start_time', value.break_start_time)}
                            />
                            <FieldError touched={touched} errors={errors} field={'break_start_time'} />
                        </div>

                        <div className="pl-8 w-full pt-[5px]">
                            <TimePicker
                                label={t('break_end_time')}
                                name="break_end_time"
                                value={values.break_end_time}
                                onChange={(value) => setFieldValue('break_end_time', value.break_end_time)}
                            />
                            <FieldError touched={touched} errors={errors} field={'break_end_time'} />
                        </div>
                    </div>

                    <div className="flex w-full justify-between">
                        <div className="w-full pt-[5px]">
                            <Input
                                label={t('break_duration_hours')}
                                type="number"
                                placeholder=""
                                name="break_duration_hours"
                                value={values.break_duration_hours}
                                onChange={(value) => setFieldValue('break_duration_hours', value.break_duration_hours)}
                            />
                            <FieldError touched={touched} errors={errors} field={'break_duration_hours'} />
                        </div>

                        <div className="pl-8 w-full pt-[5px]">
                            <Input
                                label={t('estimated_visit_time_minutes')}
                                type="number"
                                placeholder=""
                                name="estimated_visit_time_minutes"
                                value={values.estimated_visit_time_minutes}
                                onChange={(value) =>
                                    setFieldValue('estimated_visit_time_minutes', value.estimated_visit_time_minutes)
                                }
                            />
                            <FieldError touched={touched} errors={errors} field={'estimated_visit_time_minutes'} />
                        </div>
                    </div>

                    <div className="flex w-full justify-between">
                        <div className="w-full pt-[5px]">
                            <Input
                                label={t('max_distance_per_day_km')}
                                type="number"
                                placeholder=""
                                name="max_distance_per_day_km"
                                value={values.max_distance_per_day_km}
                                onChange={(value) => setFieldValue('max_distance_per_day_km', value.max_distance_per_day_km)}
                            />
                            <FieldError touched={touched} errors={errors} field={'max_distance_per_day_km'} />
                        </div>
                    </div>

                    <div className="pt-8 flex justify-end gap-4">
                        <Button label={t('common.button.cancel')} onClick={() => navigate(-1)} />
                        <button
                            type="submit"
                            onClick={(e) => {
                                e.preventDefault();
                                submitForm();
                            }}
                            className="inline-flex items-center border h-[32px] text-sm leading-4 font-medium rounded-[20px] px-3 py-2 border-transparent shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-transparent cursor-pointer"
                        >
                            {t(isEdit ? 'common.button.save' : 'common.form-title.new')}
                        </button>
                    </div>
                </form>
            )}
        </Formik>
    );
};